import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { NumericFormat } from 'react-number-format';
import { Modal, Input, Typography, Button } from 'antd';
import * as yup from 'yup';
import { loadFromLocalStorageObjectFromBase64 } from '@databases/localStorage';
import { contantAuthentication } from '@contants/index';
import { apiUpdateConfigV2 } from '@services/ManageDevices/DeviceServices';
import { showSuccess } from '@components/notification';

function ModalAdjustConsumption({ open, onClose, record, fetchData }) {
  const [loading, setLoading] = useState(false);
  const [adjustedConsumptionValue, setAdjustedConsumptionValue] = useState('');
  const [remarks, setRemarks] = useState(null);
  const [adjustedBy, setAdjustedBy] = useState('');
  const [errors, setErrors] = useState({}); // State to track validation errors

  const user = JSON.parse(loadFromLocalStorageObjectFromBase64(contantAuthentication.DATA_AUTH));

  // Synchronize values with `record` when modal opens
  useEffect(() => {
    if (open && record) {
      setAdjustedConsumptionValue(record?.adjusted_consumption_value ?? '');
      setRemarks(record?.remarks ?? '');
      setAdjustedBy(user?.user?.user_name ?? '');
      setErrors({}); // Clear errors when modal opens
    }
  }, [open, record]);

  const validateInputs = () => {
    const validationSchema = yup.object().shape({
      adjusted_consumption_value: yup
        .number()
        .transform((value, originalValue) =>
          originalValue?.toString()?.trim() === '' ? null : value
        ) // Convert empty string to null
        .required('Vui lòng nhập số liệu điều chỉnh')
        .nullable(),
      remarks: yup.string().max(500, 'Ghi chú không được vượt quá 500 ký tự')
    });

    try {
      validationSchema.validateSync(
        { adjusted_consumption_value: adjustedConsumptionValue, remarks },
        { abortEarly: false }
      );
      setErrors({});
      return true;
    } catch (validationErrors) {
      const errorMessages = {};
      validationErrors.inner.forEach((error) => {
        errorMessages[error.path] = error.message;
      });
      setErrors(errorMessages);
      return false;
    }
  };

  const handleSubmit = async () => {
    if (!validateInputs()) return;

    try {
      setLoading(true);

      // Call API to update
      await apiUpdateConfigV2(
        {
          adjusted_consumption_value: Number(adjustedConsumptionValue),
          remarks,
          adjusted_by: adjustedBy
        },
        record?.config_id
      );
      showSuccess({ message: 'Điều chỉnh thành công' });
      onClose();
      fetchData();
    } catch (error) {
      console.error('Validation or API error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <div>
          <div>Điều chỉnh tiêu thụ</div>
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            Khách hàng {record?.customer_name} ({record?.customer_id})
          </Typography.Text>
        </div>
      }
      visible={open}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={600}
      footer={[
        <Button
          key="back"
          style={{ height: '36px', backgroundColor: '#E8EBEF', color: '#192434' }}
          onClick={onClose}>
          Hủy bỏ
        </Button>,
        <Button
          key="submit"
          style={{ backgroundColor: '#0051A9', color: '#FFFF', height: '36px' }}
          loading={loading}
          onClick={handleSubmit}>
          Điều chỉnh
        </Button>
      ]}>
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px' }}>Số liệu điều chỉnh (m3) *</label>
        <NumericFormat
          style={{
            width: '100%',
            height: '36px',
            padding: '8px',
            borderRadius: 0,
            border: '1px solid #D9D9D9'
          }}
          placeholder="Nhập số liệu điều chỉnh"
          thousandSeparator=","
          decimalSeparator="."
          decimalScale={3}
          value={adjustedConsumptionValue}
          onValueChange={(value) => setAdjustedConsumptionValue(value.floatValue)}
        />
        {errors.adjusted_consumption_value && (
          <Typography.Text type="danger" color="red">
            {errors.adjusted_consumption_value}
          </Typography.Text>
        )}
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px' }}>Ghi chú</label>
        <Input.TextArea
          rows={4}
          placeholder="Nhập ghi chú (tối đa 500 ký tự)"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
        />
        {errors.remarks && (
          <Typography.Text type="danger" color="red">
            {errors.remarks}
          </Typography.Text>
        )}
      </div>
    </Modal>
  );
}

ModalAdjustConsumption.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  record: PropTypes.object,
  fetchData: PropTypes.func
};

export default ModalAdjustConsumption;
